/**
 * useFocusTrap — keeps Tab inside a container while it is open, and hands
 * focus back to whatever opened it on close ("Lightbox / full view:
 * focus-trapped, focus restored on close").
 *
 * Deliberately not a Radix Dialog: full view restyles the *existing* theater
 * subtree in place so the `<video>` element survives, and a Dialog would
 * portal it somewhere else and remount the player.
 */
import { useEffect, type RefObject } from 'react';

import { useDocumentEvent } from '../../../../../lib/browser/useEventListener';

const TAB_KEY = 'Tab';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface FocusTrapOptions {
  /** Focused when the trap opens; falls back to the first focusable element. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/** Whether the element takes part in the page: not `hidden`, `display:none`, `visibility:hidden` or inert. */
function isReachable(element: HTMLElement): boolean {
  if (element.closest('[inert]')) return false;
  if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true });
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return getComputedStyle(element).visibility !== 'hidden';
}

/** Tab stops in the container — a hidden control is never one, or the wrap would land focus on nothing. */
function focusablesWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isReachable);
}

/** Whatever has focus in the container's document, when it can take focus back. */
function focusedElementFor(container: HTMLElement): HTMLElement | null {
  const focused = container.ownerDocument.activeElement;
  return focused instanceof HTMLElement ? focused : null;
}

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean, options: FocusTrapOptions = {}): void {
  const { initialFocusRef } = options;

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    const opener = focusedElementFor(container);
    const entry = initialFocusRef?.current ?? focusablesWithin(container).at(0);
    entry?.focus();

    return () => {
      // A no-op when the opener has since been unmounted (the whole panel closed).
      opener?.focus();
    };
  }, [active, containerRef, initialFocusRef]);

  function wrapTab(e: KeyboardEvent): void {
    if (e.key !== TAB_KEY) return;
    const container = containerRef.current;
    if (!container) return;
    const focusables = focusablesWithin(container);
    const first = focusables.at(0);
    const last = focusables.at(-1);
    if (!first || !last) return;

    const focused = container.ownerDocument.activeElement;
    const escaped = !container.contains(focused);

    if (e.shiftKey && (escaped || focused === first)) {
      e.preventDefault();
      last.focus();
      return;
    }
    if (!e.shiftKey && (escaped || focused === last)) {
      e.preventDefault();
      first.focus();
    }
  }

  // Capture phase, so the wrap happens before anything inside reacts to Tab.
  useDocumentEvent('keydown', wrapTab, { enabled: active, capture: true });
}
